import { useState } from 'react';
import { Outlet } from 'react-router-dom';

import { FullPageSpinner } from '@/components/ui/Spinner';
import { useAuth } from '@/features/auth/useAuth';
import { useProfile } from '@/features/auth/useProfile';
import { Sidebar } from './Sidebar';
import { ConnectionBanner } from './ConnectionBanner';
import { TopBar } from './TopBar';

/**
 * The authenticated layout: a permanent sidebar on desktop, a drawer on phones.
 *
 * Everything positional uses logical properties (`border-e`, `start-0`), so the
 * drawer slides in from the right in Arabic and the left in English with no extra
 * code — which is the whole reason physical directions are banned by the linter.
 */
export function AppShell() {
  const [menuOpen, setMenuOpen] = useState(false);
  const { signOut } = useAuth();
  const { data: profile } = useProfile();

  if (!profile) return <FullPageSpinner />;

  return (
    <div className="flex min-h-dvh flex-col">
      <ConnectionBanner />

      <TopBar
        profile={profile}
        onToggleMenu={() => setMenuOpen((open) => !open)}
        onSignOut={() => void signOut()}
      />

      <div className="flex flex-1">
        <aside className="hidden w-60 shrink-0 border-e border-slate-200 bg-white md:block">
          <Sidebar role={profile.role} />
        </aside>

        {menuOpen ? (
          <div className="fixed inset-0 z-40 md:hidden">
            <button
              type="button"
              aria-label="close menu"
              className="absolute inset-0 bg-slate-900/40"
              onClick={() => setMenuOpen(false)}
            />
            <aside className="absolute bottom-0 start-0 top-14 w-60 border-e border-slate-200 bg-white shadow-xl">
              <Sidebar role={profile.role} onNavigate={() => setMenuOpen(false)} />
            </aside>
          </div>
        ) : null}

        <main className="min-w-0 flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
